export type ShareChannel =
	| 'facebook'
	| 'facebookMessenger'
	| 'twitter'
	| 'whatsapp'
	| 'instagram'
	| 'instagramStories'
	| 'tiktok'
	| 'vk'
	| 'line'
	| 'nextdoor'
	| 'email'
	| 'sms'
	| 'copy'
	| 'mobileNativeShare'
	| 'reddit';

/**
 * A share asset paired with the variation it was generated from. Kept as one object rather than two
 * parallel arrays: assets that fail to convert are dropped, so a separate name list would silently
 * drift out of alignment and mislabel every tracked share.
 */
export type ShareAsset = {
	file: File;
	variationName: string;
};

export type ShareAssetPicker = {
	/**
	 * Which picker to render. Describes a presentation, not an experiment arm — the shared share module
	 * stays unaware of which variation asked for it.
	 */
	layout: 'carousel' | 'swatches';
	/** Resolves the already-warmed assets — see `useWarmStoryShareAssets`. */
	loadAssets: () => Promise<readonly ShareAsset[]>;
	/**
	 * Every variation the picker intends to offer. Reported alongside what actually rendered so a supporter
	 * who was shown no picker is distinguishable from one who saw it and ignored it — those two are the
	 * same event shape otherwise, and only one of them is evidence about the treatment.
	 */
	offeredVariations: readonly string[];
	/**
	 * Fired when the supporter changes the settled asset. Tracking lives on the page rather than in here
	 * so the shared share components stay unaware of the experiment.
	 */
	onAssetSelected: (selection: ShareAssetSelection) => void;
};

export type ShareAssetSelection = {
	variationName: string;
	/** Zero-based position in the picker. */
	position: number;
	total: number;
	interactionType: 'swipe' | 'tap';
};

/** All props need to be optional to allow for flexibility in usage. */
export type ShareConfig = {
	instagramStories?: {
		enabled: boolean;
		/**
		 * Opts the story modal into the multiple-vertical-share-assets picker (CHANGE-95456). Absent for
		 * every caller except the promote-or-share treatment arms, which keeps the single-asset modal.
		 *
		 * Lives on `config` rather than widening `getContent`'s `image` to an array — `getContent` is shared
		 * by every channel and surface, so widening it ripples into petitionDetails and starter dashboard.
		 */
		picker?: ShareAssetPicker;
	};
	tiktok?: {
		enabled: boolean;
	};
	facebook?: { mode: { mobile: 'sdk' | 'url'; desktop: 'sdk' | 'url' } };
	mobileNativeShare?: { trackIntent?: boolean };
};

/**
 * Opt in to `share_petition_intent`-style tracking for the `mobileNativeShare` channel.
 * Shared so the petition-share touchpoints that opt in (see CHANGE-95044) stay in sync.
 */
export const TRACK_NATIVE_SHARE_INTENT: NonNullable<ShareConfig['mobileNativeShare']> = { trackIntent: true };
